"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Suspense } from "react";
import { Globe } from "lucide-react";
import { LOCALES, LOCALE_META, type Locale } from "@/lib/i18n/config";
import { useLocale, useMessages } from "@/lib/i18n/client";
import { switchLocalePath } from "@/lib/i18n/paths";
import { saveLocalePreference } from "@/lib/i18n/preference";
import { updateProfile } from "@/lib/api";
import { useAuthStore } from "@/lib/store/auth-store";
import { cn } from "@/lib/utils";
import messages from "./language-switcher.i18n";
import { track } from "@/lib/tracking/track";

// Selector ES/EN: misma página en el otro idioma, conservando la query. La
// elección se guarda (cookie) para que las URLs sin prefijo redirijan a ella.
function LanguageSwitcherInner({ className }: { className?: string }) {
  const lang = useLocale();
  const t = useMessages(messages);
  const router = useRouter();
  const pathname = usePathname() ?? "/";
  const searchParams = useSearchParams();
  const loggedIn = useAuthStore((s) => !!s.user);

  function change(to: Locale) {
    if (to === lang) return;
    saveLocalePreference(to);
    track("language_change", { language: to });
    // Con sesión: los correos pasan a llegarle en este idioma. Si falla no
    // importa — se vuelve a guardar en el próximo login.
    if (loggedIn) updateProfile({ language: to }).catch(() => {});
    const query = searchParams?.toString();
    router.push(`${switchLocalePath(pathname, to)}${query ? `?${query}` : ""}`);
  }

  return (
    <div role="group" aria-label={t.label} className={cn("flex items-center gap-1 text-xs", className)}>
      <Globe className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
      {LOCALES.map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => change(l)}
          aria-pressed={l === lang}
          title={LOCALE_META[l].label}
          className={cn(
            "rounded px-1.5 py-0.5 font-semibold transition hover:cursor-pointer",
            l === lang ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {LOCALE_META[l].short}
        </button>
      ))}
    </div>
  );
}

// useSearchParams exige Suspense para no forzar render dinámico.
export function LanguageSwitcher({ className }: { className?: string }) {
  return (
    <Suspense fallback={null}>
      <LanguageSwitcherInner className={className} />
    </Suspense>
  );
}
